// industryConfigs.js
// Configuration for investment preference dimensions across all industries

export const INDUSTRY_CONFIGS = {
  renewables: {
    id: 'renewables',
    label: 'Renewables',
    icon: '⚡',
    dimensions: [
      {
        key: 'technologies',
        label: 'Technologies',
        type: 'multi-select',
        icon: 'Zap',
        options: [
          'Solar PV',
          'Onshore Wind',
          'Offshore Wind',
          'Storage / BESS',
          'Hydrogen',
          'Hydro',
          'Geothermal',
          'Biomass',
          'Waste-to-Energy'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'dealStage',
        label: 'Deal Stage',
        type: 'multi-select',
        icon: 'TrendingUp',
        options: [
          'Early Development',
          'Development',
          'Permitted / RTB',
          'Construction',
          'Operating / COD'
        ]
      },
      {
        key: 'dealSize',
        label: 'Project Size',
        type: 'range',
        icon: 'BarChart3',
        unit: 'MW',
        defaultMin: 0,
        defaultMax: 1000
      },
      {
        key: 'dealStructure',
        label: 'Deal Structure',
        type: 'multi-select',
        icon: 'GitBranch',
        options: [
          'Asset Acquisition',
          'Project Rights',
          'Platform / M&A',
          'JV / Partnership',
          'Minority Stake',
          'Development Partnership'
        ]
      },
      {
        key: 'offtake',
        label: 'Offtake Preference',
        type: 'multi-select',
        icon: 'FileText',
        options: [
          'Merchant',
          'PPA Required',
          'CfD / Subsidy',
          'Utility PPA',
          'Corporate PPA',
          'Flexible'
        ]
      }
    ],
    extractionHints: [
      'Look for MW capacity mentions',
      'Identify technology focus from portfolio',
      'Note any geographic expansion or exit announcements',
      'Check for PPA or merchant strategy mentions'
    ]
  },

  real_estate: {
    id: 'real_estate',
    label: 'Real Estate',
    icon: '🏢',
    dimensions: [
      {
        key: 'assetClasses',
        label: 'Asset Classes',
        type: 'multi-select',
        icon: 'Building2',
        options: [
          'Office',
          'Retail',
          'Industrial / Logistics',
          'Residential / Multifamily',
          'Student Housing',
          'Senior Living',
          'Hospitality',
          'Mixed-Use',
          'Data Centers',
          'Life Sciences',
          'Self-Storage'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'strategy',
        label: 'Investment Strategy',
        type: 'multi-select',
        icon: 'Target',
        options: [
          'Core',
          'Core+',
          'Value-Add',
          'Opportunistic',
          'Development',
          'Distressed'
        ]
      },
      {
        key: 'ticketSize',
        label: 'Ticket Size',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 500
      },
      {
        key: 'yieldTarget',
        label: 'Yield Target',
        type: 'range',
        icon: 'Percent',
        unit: '%',
        defaultMin: 3,
        defaultMax: 15
      },
      {
        key: 'holdPeriod',
        label: 'Hold Period',
        type: 'multi-select',
        icon: 'Clock',
        options: [
          'Short-term (<3 years)',
          'Medium-term (3-7 years)',
          'Long-term (7+ years)',
          'Perpetual / Open-ended'
        ]
      }
    ],
    extractionHints: [
      'Look for AUM and fund size mentions',
      'Identify asset class focus from portfolio',
      'Note core vs value-add strategy',
      'Check for yield or return targets'
    ]
  },

  venture_capital: {
    id: 'venture_capital',
    label: 'Venture Capital',
    icon: '🚀',
    dimensions: [
      {
        key: 'sectors',
        label: 'Sectors',
        type: 'multi-select',
        icon: 'Layers',
        options: [
          'SaaS / Enterprise Software',
          'Fintech',
          'Healthtech / Biotech',
          'Climate / Cleantech',
          'AI / ML',
          'Consumer',
          'E-commerce / Marketplaces',
          'Deeptech / Hardware',
          'Web3 / Crypto',
          'Gaming',
          'Edtech',
          'Proptech',
          'Mobility'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'stage',
        label: 'Investment Stage',
        type: 'multi-select',
        icon: 'TrendingUp',
        options: [
          'Pre-Seed',
          'Seed',
          'Series A',
          'Series B',
          'Series C+',
          'Growth'
        ]
      },
      {
        key: 'checkSize',
        label: 'Check Size',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 50
      },
      {
        key: 'leadFollow',
        label: 'Lead / Follow',
        type: 'single-select',
        icon: 'Flag',
        options: [
          'Lead Only',
          'Prefer to Lead',
          'Can Lead or Follow',
          'Follow Only'
        ]
      },
      {
        key: 'model',
        label: 'Business Model',
        type: 'multi-select',
        icon: 'Briefcase',
        options: [
          'B2B',
          'B2C',
          'B2B2C',
          'Marketplace',
          'Hardware + Software',
          'Deep Tech'
        ]
      }
    ],
    extractionHints: [
      'Look for portfolio company sectors',
      'Identify stage focus from recent investments',
      'Note check size from press releases',
      'Check if they lead rounds or follow'
    ]
  },

  private_equity: {
    id: 'private_equity',
    label: 'Private Equity',
    icon: '💼',
    dimensions: [
      {
        key: 'sectors',
        label: 'Sectors',
        type: 'multi-select',
        icon: 'Layers',
        options: [
          'Technology',
          'Healthcare',
          'Industrials',
          'Consumer',
          'Financial Services',
          'Business Services',
          'Energy',
          'Infrastructure',
          'Media / Telecom',
          'Education'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'dealType',
        label: 'Deal Type',
        type: 'multi-select',
        icon: 'GitMerge',
        options: [
          'Buyout',
          'Growth Equity',
          'Carve-out',
          'Secondary',
          'Recapitalization',
          'PIPE',
          'Take-Private'
        ]
      },
      {
        key: 'ebitdaRange',
        label: 'Target EBITDA',
        type: 'range',
        icon: 'BarChart3',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 200
      },
      {
        key: 'evRange',
        label: 'Enterprise Value',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 2000
      },
      {
        key: 'control',
        label: 'Control Preference',
        type: 'single-select',
        icon: 'Shield',
        options: [
          'Control Only',
          'Prefer Control',
          'Flexible',
          'Minority OK'
        ]
      }
    ],
    extractionHints: [
      'Look for fund size and strategy',
      'Identify sector focus from portfolio',
      'Note EBITDA or revenue thresholds',
      'Check for control vs minority preference'
    ]
  },

  infrastructure: {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: '🌉',
    dimensions: [
      {
        key: 'sectors',
        label: 'Sectors',
        type: 'multi-select',
        icon: 'Layers',
        options: [
          'Transport (Roads, Rail, Airports)',
          'Energy / Utilities',
          'Digital / Telecom / Fiber',
          'Social Infrastructure',
          'Water / Waste',
          'Renewables',
          'Midstream / Pipelines',
          'Ports / Logistics'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'assetProfile',
        label: 'Asset Profile',
        type: 'multi-select',
        icon: 'Box',
        options: [
          'Greenfield',
          'Brownfield',
          'Operating / Stabilized',
          'Platform / Portfolio'
        ]
      },
      {
        key: 'regulatoryProfile',
        label: 'Regulatory Profile',
        type: 'multi-select',
        icon: 'FileCheck',
        options: [
          'Regulated / RAB',
          'Contracted / Availability',
          'Merchant / GDP-linked',
          'Hybrid'
        ]
      },
      {
        key: 'evRange',
        label: 'Enterprise Value',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 5000
      },
      {
        key: 'returnProfile',
        label: 'Return Profile',
        type: 'multi-select',
        icon: 'TrendingUp',
        options: [
          'Core (6-8% IRR)',
          'Core+ (8-12% IRR)',
          'Value-Add (12-15% IRR)',
          'Opportunistic (15%+ IRR)'
        ]
      }
    ],
    extractionHints: [
      'Look for infrastructure fund mandates',
      'Identify sector focus from portfolio',
      'Note regulated vs merchant preference',
      'Check for greenfield vs brownfield focus'
    ]
  },

  ma_corporate: {
    id: 'ma_corporate',
    label: 'M&A / Corporate',
    icon: '🤝',
    dimensions: [
      {
        key: 'sectors',
        label: 'Sectors of Interest',
        type: 'multi-select',
        icon: 'Layers',
        options: [
          'Technology',
          'Healthcare',
          'Industrials',
          'Consumer',
          'Financial Services',
          'Energy',
          'Media / Entertainment',
          'Retail',
          'Automotive'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'dealType',
        label: 'Deal Type',
        type: 'multi-select',
        icon: 'GitMerge',
        options: [
          'Strategic Acquisition',
          'Divestiture / Spin-off',
          'Merger',
          'Joint Venture',
          'Carve-out',
          'Asset Purchase'
        ]
      },
      {
        key: 'role',
        label: 'Typical Role',
        type: 'multi-select',
        icon: 'Users',
        options: [
          'Acquirer',
          'Seller / Divesting',
          'Target',
          'JV Partner',
          'Advisor'
        ]
      },
      {
        key: 'dealSize',
        label: 'Deal Size',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 5000
      },
      {
        key: 'rationale',
        label: 'Strategic Rationale',
        type: 'multi-select',
        icon: 'Target',
        options: [
          'Market Expansion',
          'Technology Acquisition',
          'Vertical Integration',
          'Horizontal Consolidation',
          'Cost Synergies',
          'Portfolio Optimization'
        ]
      }
    ],
    extractionHints: [
      'Look for corporate strategy announcements',
      'Identify M&A track record',
      'Note whether typically buyer or seller',
      'Check for stated strategic priorities'
    ]
  },

  data_centers: {
    id: 'data_centers',
    label: 'Data Centers',
    icon: '🖥️',
    dimensions: [
      {
        key: 'dcType',
        label: 'Data Center Type',
        type: 'multi-select',
        icon: 'Server',
        options: [
          'Hyperscale',
          'Colocation',
          'Enterprise',
          'Edge',
          'Carrier Hotel'
        ]
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'dealStage',
        label: 'Deal Stage',
        type: 'multi-select',
        icon: 'TrendingUp',
        options: [
          'Land / Development Site',
          'Pre-Construction',
          'Under Construction',
          'Stabilized / Leased',
          'Value-Add'
        ]
      },
      {
        key: 'capacity',
        label: 'IT Capacity',
        type: 'range',
        icon: 'Cpu',
        unit: 'MW',
        defaultMin: 0,
        defaultMax: 500
      },
      {
        key: 'dealSize',
        label: 'Investment Size',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 2000
      },
      {
        key: 'tenantProfile',
        label: 'Tenant Preference',
        type: 'multi-select',
        icon: 'Users',
        options: [
          'Hyperscaler Anchor',
          'Enterprise Mix',
          'Wholesale',
          'Retail Colocation',
          'Flexible'
        ]
      }
    ],
    extractionHints: [
      'Look for MW capacity mentions',
      'Identify hyperscale vs colo focus',
      'Note geographic expansion plans',
      'Check for anchor tenant requirements'
    ]
  },

  custom: {
    id: 'custom',
    label: 'Custom',
    icon: '⚙️',
    dimensions: [
      {
        key: 'focus',
        label: 'Investment Focus',
        type: 'freetext',
        icon: 'Target'
      },
      {
        key: 'geographies',
        label: 'Geographies',
        type: 'geo-status',
        icon: 'Globe',
        statusOptions: ['expanding', 'active', 'selective', 'divesting', 'exited']
      },
      {
        key: 'dealSize',
        label: 'Deal Size',
        type: 'range',
        icon: 'DollarSign',
        unit: 'EUR M',
        defaultMin: 0,
        defaultMax: 1000
      },
      {
        key: 'notes',
        label: 'Additional Criteria',
        type: 'freetext',
        icon: 'FileText'
      }
    ],
    extractionHints: [
      'Extract any investment criteria mentioned',
      'Note sector or asset type focus',
      'Identify geographic preferences',
      'Look for deal size indications'
    ]
  }
};

// Helper to get config by industry ID
export function getIndustryConfig(industryId) {
  return INDUSTRY_CONFIGS[industryId] || INDUSTRY_CONFIGS.custom;
}

// Get all industry options for dropdown
export function getIndustryOptions() {
  return Object.values(INDUSTRY_CONFIGS).map(config => ({
    value: config.id,
    label: config.label,
    icon: config.icon
  }));
}

// Map Global Infrastructure Sherpa industry names to config IDs
export const INDUSTRY_ID_MAP = {
  'Renewables': 'renewables',
  'Energy & Power': 'renewables',
  'Real Estate': 'real_estate',
  'Properties': 'real_estate',
  'Venture Capital': 'venture_capital',
  'Venture Cap...': 'venture_capital',
  'Startups': 'venture_capital',
  'Private Equity': 'private_equity',
  'Buyouts': 'private_equity',
  'Infrastructure': 'infrastructure',
  'M&A / Corp...': 'ma_corporate',
  'M&A / Corporate': 'ma_corporate',
  'Data Centers': 'data_centers',
  'Custom': 'custom',
  'All Industries': 'custom'
};

export function mapIndustryToConfigId(industryName) {
  return INDUSTRY_ID_MAP[industryName] || 'custom';
}
